import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SparklesIcon, RouteIcon, TrashIcon, PlusIcon, SendIcon, CheckCircleIcon, BookOpenIcon, PencilIcon, ChevronUpIcon, ChevronDownIcon, LayoutIcon, Loader2Icon } from 'lucide-react';
interface KnowledgeUnit {
  id: string;
  title: string;
}
interface JourneyTopic {
  id: string;
  title: string;
  knowledgeUnits: KnowledgeUnit[];
  estimatedSlides: number;
}
interface JourneyPlan {
  topics: JourneyTopic[];
}
interface ChatMessage {
  id: string;
  role: 'ai' | 'user';
  content: string;
  timestamp: Date;
}
interface JourneyPlanningScreenProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (plan: JourneyPlan) => void;
  journeyTitle: string;
  sourceSummaries: Array<{
    type: string;
    label: string;
  }>;
}
const generateId = () => Math.random().toString(36).substring(2, 10);
const initialTopics: JourneyTopic[] = [{
  id: generateId(),
  title: 'Introduction & Overview',
  knowledgeUnits: [{
    id: generateId(),
    title: 'What is this topic and why it matters'
  }, {
    id: generateId(),
    title: 'Key terminology and definitions'
  }, {
    id: generateId(),
    title: 'Learning objectives and outcomes'
  }],
  estimatedSlides: 4
}, {
  id: generateId(),
  title: 'Core Concepts',
  knowledgeUnits: [{
    id: generateId(),
    title: 'Fundamental principles explained'
  }, {
    id: generateId(),
    title: 'Key theories and frameworks'
  }, {
    id: generateId(),
    title: 'Visual diagrams and models'
  }, {
    id: generateId(),
    title: 'Worked examples with step-by-step solutions'
  }],
  estimatedSlides: 6
}, {
  id: generateId(),
  title: 'Practical Applications',
  knowledgeUnits: [{
    id: generateId(),
    title: 'Real-world use cases and scenarios'
  }, {
    id: generateId(),
    title: 'Guided practice problems'
  }, {
    id: generateId(),
    title: 'Common mistakes and how to avoid them'
  }],
  estimatedSlides: 5
}, {
  id: generateId(),
  title: 'Advanced Topics',
  knowledgeUnits: [{
    id: generateId(),
    title: 'Extended concepts and deeper analysis'
  }, {
    id: generateId(),
    title: 'Cross-topic connections'
  }, {
    id: generateId(),
    title: 'Challenge problems and critical thinking'
  }],
  estimatedSlides: 5
}, {
  id: generateId(),
  title: 'Review & Assessment',
  knowledgeUnits: [{
    id: generateId(),
    title: 'Summary of key takeaways'
  }, {
    id: generateId(),
    title: 'Self-assessment quiz and reflection'
  }],
  estimatedSlides: 3
}];
const aiResponses = ["Done! I've updated the plan based on your feedback. Take a look at the changes above.", "Great suggestion! I've refined the topics accordingly. Feel free to make any further adjustments.", "I've made those changes. The plan is looking solid — review the updated structure above.", "Updated! I've reorganized the knowledge units as requested. Let me know if you'd like more changes."];
const suggestedPrompts = ['Add more detail to Topic 2', 'Split Topic 3 into two', 'Add a recap topic', 'Make it more beginner-friendly'];
const ThinkingIndicator = () => <div className="flex items-center gap-1 px-3 py-2" data-id="element-1577">
    <motion.div className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" animate={{
    opacity: [0.3, 1, 0.3]
  }} transition={{
    duration: 1.2,
    repeat: Infinity,
    delay: 0
  }} data-id="element-1578" />
    <motion.div className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" animate={{
    opacity: [0.3, 1, 0.3]
  }} transition={{
    duration: 1.2,
    repeat: Infinity,
    delay: 0.2
  }} data-id="element-1579" />
    <motion.div className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" animate={{
    opacity: [0.3, 1, 0.3]
  }} transition={{
    duration: 1.2,
    repeat: Infinity,
    delay: 0.4
  }} data-id="element-1580" />
  </div>;
const AiAvatar = () => <div className="w-8 h-8 rounded-full bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0" data-id="element-1581">
    <SparklesIcon size={14} className="text-[#2563EB]" data-id="element-1582" />
  </div>;
interface TopicCardProps {
  topic: JourneyTopic;
  index: number;
  totalTopics: number;
  onUpdateTitle: (title: string) => void;
  onDeleteTopic: () => void;
  onMoveTopic: (direction: 'up' | 'down') => void;
  onUpdateKU: (kuId: string, title: string) => void;
  onDeleteKU: (kuId: string) => void;
  onAddKU: () => void;
}
const TopicCard = ({
  topic,
  index,
  totalTopics,
  onUpdateTitle,
  onDeleteTopic,
  onMoveTopic,
  onUpdateKU,
  onDeleteKU,
  onAddKU
}: TopicCardProps) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState(topic.title);
  const [editingKuId, setEditingKuId] = useState<string | null>(null);
  const [editKuTitle, setEditKuTitle] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);
  const kuInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);
  useEffect(() => {
    if (editingKuId && kuInputRef.current) {
      kuInputRef.current.focus();
      kuInputRef.current.select();
    }
  }, [editingKuId]);
  const handleTitleSave = () => {
    if (editTitle.trim()) {
      onUpdateTitle(editTitle.trim());
    } else {
      setEditTitle(topic.title);
    }
    setIsEditingTitle(false);
  };
  const handleKuSave = (kuId: string) => {
    if (editKuTitle.trim()) {
      onUpdateKU(kuId, editKuTitle.trim());
    }
    setEditingKuId(null);
  };
  const startEditKu = (ku: KnowledgeUnit) => {
    setEditingKuId(ku.id);
    setEditKuTitle(ku.title);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    delay: index * 0.08
  }} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden group/card hover:shadow-md transition-shadow" data-id="element-1583">
      {/* Topic Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#2563EB]/[0.03] to-transparent border-b border-gray-100" data-id="element-1584">
        <div className="w-7 h-7 rounded-lg bg-[#2563EB] text-white flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-1585">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0" data-id="element-1586">
          {isEditingTitle ? <input ref={titleInputRef} type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)} onBlur={handleTitleSave} onKeyDown={e => {
          if (e.key === 'Enter') handleTitleSave();
          if (e.key === 'Escape') {
            setEditTitle(topic.title);
            setIsEditingTitle(false);
          }
        }} className="w-full text-sm font-semibold text-text-primary bg-white border border-[#2563EB]/30 rounded-lg px-2.5 py-1 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40" data-id="element-1587" /> : <button onClick={() => {
          setEditTitle(topic.title);
          setIsEditingTitle(true);
        }} className="flex items-center gap-1.5 text-sm font-semibold text-text-primary hover:text-[#2563EB] transition-colors group/title text-left" aria-label={`Edit topic title: ${topic.title}`} data-id="element-1588">
              <span className="truncate" data-id="element-1589">{topic.title}</span>
              <PencilIcon size={11} className="text-text-tertiary opacity-0 group-hover/title:opacity-100 transition-opacity flex-shrink-0" data-id="element-1590" />
            </button>}
        </div>
        <div className="flex items-center gap-0.5 opacity-0 group-hover/card:opacity-100 transition-opacity" data-id="element-1591">
          {index > 0 && <button onClick={() => onMoveTopic('up')} className="p-1.5 rounded-md hover:bg-gray-100 text-text-tertiary hover:text-text-secondary transition-colors" aria-label="Move topic up" data-id="element-1592">
              <ChevronUpIcon size={14} data-id="element-1593" />
            </button>}
          {index < totalTopics - 1 && <button onClick={() => onMoveTopic('down')} className="p-1.5 rounded-md hover:bg-gray-100 text-text-tertiary hover:text-text-secondary transition-colors" aria-label="Move topic down" data-id="element-1594">
              <ChevronDownIcon size={14} data-id="element-1595" />
            </button>}
          <button onClick={onDeleteTopic} className="p-1.5 rounded-md hover:bg-red-50 text-text-tertiary hover:text-red-500 transition-colors" aria-label={`Delete topic: ${topic.title}`} data-id="element-1596">
            <TrashIcon size={14} data-id="element-1597" />
          </button>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-text-tertiary bg-gray-100 px-2 py-1 rounded-md flex-shrink-0" data-id="element-1598">
          <LayoutIcon size={10} data-id="element-1599" />
          <span data-id="element-1600">~{topic.estimatedSlides} slides</span>
        </div>
      </div>

      {/* Knowledge Units */}
      <div className="px-4 py-2.5" data-id="element-1601">
        <div className="flex items-center gap-1.5 mb-2" data-id="element-1602">
          <BookOpenIcon size={10} className="text-text-tertiary" data-id="element-1603" />
          <span className="text-[10px] font-bold text-text-tertiary uppercase tracking-wider" data-id="element-1604">
            Knowledge Units
          </span>
        </div>
        <ul className="space-y-1" role="list" data-id="element-1605">
          {topic.knowledgeUnits.map(ku => <li key={ku.id} className="group/ku flex items-start gap-2 py-1 rounded-md hover:bg-gray-50 px-1.5 -mx-1.5 transition-colors" data-id="element-1606">
              <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB]/40 mt-[7px] flex-shrink-0" data-id="element-1607" />
              {editingKuId === ku.id ? <input ref={kuInputRef} type="text" value={editKuTitle} onChange={e => setEditKuTitle(e.target.value)} onBlur={() => handleKuSave(ku.id)} onKeyDown={e => {
            if (e.key === 'Enter') handleKuSave(ku.id);
            if (e.key === 'Escape') setEditingKuId(null);
          }} className="flex-1 text-xs text-text-primary bg-white border border-[#2563EB]/30 rounded px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40" data-id="element-1608" /> : <button onClick={() => startEditKu(ku)} className="flex-1 text-xs text-text-secondary text-left hover:text-text-primary transition-colors leading-relaxed" aria-label={`Edit knowledge unit: ${ku.title}`} data-id="element-1609">
                  {ku.title}
                </button>}
              <button onClick={() => onDeleteKU(ku.id)} className="p-0.5 rounded hover:bg-red-50 text-text-tertiary hover:text-red-500 transition-colors opacity-0 group-hover/ku:opacity-100 flex-shrink-0 mt-0.5" aria-label={`Remove: ${ku.title}`} data-id="element-1610">
                <XIcon size={12} data-id="element-1611" />
              </button>
            </li>)}
        </ul>
        <button onClick={onAddKU} className="flex items-center gap-1.5 mt-2 px-2 py-1.5 text-[11px] font-medium text-[#2563EB]/70 hover:text-[#2563EB] hover:bg-[#2563EB]/5 rounded-md transition-colors" data-id="element-1612">
          <PlusIcon size={12} data-id="element-1613" />
          Add knowledge unit
        </button>
      </div>
    </motion.div>;
};
export function JourneyPlanningScreen({
  isOpen,
  onClose,
  onConfirm,
  journeyTitle,
  sourceSummaries
}: JourneyPlanningScreenProps) {
  const [topics, setTopics] = useState<JourneyTopic[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [planRevealed, setPlanRevealed] = useState(false);
  const [userInput, setUserInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const responseIndexRef = useRef(0);
  // Initialize on open
  useEffect(() => {
    if (isOpen) {
      setTopics([]);
      setPlanRevealed(false);
      setMessages([]);
      setUserInput('');
      responseIndexRef.current = 0;
      // Simulate AI thinking
      setIsThinking(true);
      const thinkTimer = setTimeout(() => {
        setIsThinking(false);
        setTopics(initialTopics.map(t => ({
          ...t,
          id: generateId(),
          knowledgeUnits: t.knowledgeUnits.map(ku => ({
            ...ku,
            id: generateId()
          }))
        })));
        setMessages([{
          id: generateId(),
          role: 'ai',
          content: "I've analyzed your content and extracted the following learning plan. Review the topics and knowledge units below — you can edit, reorder, add or remove anything. When you're happy with the structure, confirm to start generating your journey.",
          timestamp: new Date()
        }]);
        setPlanRevealed(true);
      }, 2200);
      return () => clearTimeout(thinkTimer);
    }
  }, [isOpen]);
  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        scrollRef.current?.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [messages, isThinking, planRevealed]);
  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);
  const handleSendMessage = useCallback(() => {
    const trimmed = userInput.trim();
    if (!trimmed || isThinking) return;
    const userMsg: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: trimmed,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    setUserInput('');
    setIsThinking(true);
    setTimeout(() => {
      const aiMsg: ChatMessage = {
        id: generateId(),
        role: 'ai',
        content: aiResponses[responseIndexRef.current % aiResponses.length],
        timestamp: new Date()
      };
      responseIndexRef.current += 1;
      setMessages(prev => [...prev, aiMsg]);
      setIsThinking(false);
      inputRef.current?.focus();
    }, 1500);
  }, [userInput, isThinking]);
  // Topic operations
  const updateTopicTitle = (topicId: string, title: string) => {
    setTopics(prev => prev.map(t => t.id === topicId ? {
      ...t,
      title
    } : t));
  };
  const deleteTopic = (topicId: string) => {
    setTopics(prev => prev.filter(t => t.id !== topicId));
  };
  const moveTopic = (topicId: string, direction: 'up' | 'down') => {
    setTopics(prev => {
      const idx = prev.findIndex(t => t.id === topicId);
      if (idx === -1) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const copy = [...prev];
      const temp = copy[idx];
      copy[idx] = copy[newIdx];
      copy[newIdx] = temp;
      return copy;
    });
  };
  const updateKU = (topicId: string, kuId: string, title: string) => {
    setTopics(prev => prev.map(t => t.id === topicId ? {
      ...t,
      knowledgeUnits: t.knowledgeUnits.map(ku => ku.id === kuId ? {
        ...ku,
        title
      } : ku)
    } : t));
  };
  const deleteKU = (topicId: string, kuId: string) => {
    setTopics(prev => prev.map(t => t.id === topicId ? {
      ...t,
      knowledgeUnits: t.knowledgeUnits.filter(ku => ku.id !== kuId),
      estimatedSlides: Math.max(1, t.estimatedSlides - 1)
    } : t));
  };
  const addKU = (topicId: string) => {
    setTopics(prev => prev.map(t => t.id === topicId ? {
      ...t,
      knowledgeUnits: [...t.knowledgeUnits, {
        id: generateId(),
        title: 'New knowledge unit'
      }],
      estimatedSlides: t.estimatedSlides + 1
    } : t));
  };
  const addTopic = () => {
    setTopics(prev => [...prev, {
      id: generateId(),
      title: `Topic ${prev.length + 1}`,
      knowledgeUnits: [{
        id: generateId(),
        title: 'New knowledge unit'
      }],
      estimatedSlides: 2
    }]);
  };
  const handleConfirm = () => {
    onConfirm({
      topics
    });
  };
  // Stats
  const totalTopics = topics.length;
  const totalKUs = topics.reduce((sum, t) => sum + t.knowledgeUnits.length, 0);
  const totalSlides = topics.reduce((sum, t) => sum + t.estimatedSlides, 0);
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-1614">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div key="planning-backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} data-id="element-1615" />

          {/* Modal */}
          <motion.div key="planning-modal" initial={{
        opacity: 0,
        scale: 0.97,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 16
      }} transition={{
        duration: 0.25,
        ease: [0.4, 0, 0.2, 1]
      }} className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-hidden" data-id="element-1616">
            <div className="bg-surface w-full sm:max-w-3xl sm:rounded-2xl shadow-2xl flex flex-col h-full sm:h-auto sm:max-h-[92vh] min-h-screen sm:min-h-0 overflow-hidden" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Plan Your Learning Journey" data-id="element-1617">
              {/* ─── Header ─── */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-custom flex-shrink-0 bg-gradient-to-r from-[#2563EB]/[0.03] to-transparent" data-id="element-1618">
                <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-1619">
                  <div className="w-9 h-9 rounded-xl bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0" data-id="element-1620">
                    <RouteIcon size={18} className="text-[#2563EB]" data-id="element-1621" />
                  </div>
                  <div className="min-w-0" data-id="element-1622">
                    <h2 className="text-base sm:text-lg font-bold text-text-primary truncate" data-id="element-1623">
                      Plan Your Learning Journey
                    </h2>
                    <p className="text-xs text-text-tertiary truncate mt-0.5" data-id="element-1624">
                      {journeyTitle}
                      {sourceSummaries.length > 0 && <span className="text-text-tertiary" data-id="element-1625">
                          {' '}
                          · {sourceSummaries.length} source
                          {sourceSummaries.length > 1 ? 's' : ''} analyzed
                        </span>}
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 -mr-1 rounded-lg hover:bg-surface-elevated text-text-tertiary hover:text-text-primary transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0 ml-2" aria-label="Close planning screen" data-id="element-1626">
                  <XIcon size={20} data-id="element-1627" />
                </button>
              </div>

              {/* ─── Chat Thread (Scrollable) ─── */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-5" data-id="element-1628">
                <div className="max-w-2xl mx-auto space-y-5" data-id="element-1629">
                  {/* Initial thinking state */}
                  {!planRevealed && isThinking && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} className="flex items-start gap-3" data-id="element-1630">
                      <AiAvatar data-id="element-1631" />
                      <div className="bg-[#2563EB]/[0.04] border border-[#2563EB]/10 rounded-xl rounded-tl-sm px-4 py-3" data-id="element-1632">
                        <div className="flex items-center gap-2 mb-1" data-id="element-1633">
                          <span className="text-xs font-semibold text-[#2563EB]" data-id="element-1634">
                            Ainstein AI
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-text-tertiary" data-id="element-1635">
                          <Loader2Icon size={12} className="animate-spin text-[#2563EB]" data-id="element-1636" />
                          Analyzing your content and extracting topics...
                        </div>
                        <ThinkingIndicator data-id="element-1637" />
                      </div>
                    </motion.div>}

                  {/* AI Messages + Plan */}
                  {messages.map((msg, msgIdx) => <motion.div key={msg.id} initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-1638">
                      {msg.role === 'ai' ? <div className="flex items-start gap-3" data-id="element-1639">
                          <AiAvatar data-id="element-1640" />
                          <div className="flex-1 min-w-0" data-id="element-1641">
                            <div className="bg-[#2563EB]/[0.04] border border-[#2563EB]/10 rounded-xl rounded-tl-sm px-4 py-3 mb-4" data-id="element-1642">
                              <div className="flex items-center gap-2 mb-1.5" data-id="element-1643">
                                <span className="text-xs font-semibold text-[#2563EB]" data-id="element-1644">
                                  Ainstein AI
                                </span>
                                <span className="text-[10px] text-text-tertiary" data-id="element-1645">
                                  {msg.timestamp.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                                </span>
                              </div>
                              <p className="text-sm text-text-secondary leading-relaxed" data-id="element-1646">
                                {msg.content}
                              </p>
                            </div>

                            {/* Show topic plan after the first AI message */}
                            {msgIdx === 0 && planRevealed && <div className="space-y-3" data-id="element-1647">
                                {topics.map((topic, topicIdx) => <TopicCard key={topic.id} topic={topic} index={topicIdx} totalTopics={topics.length} onUpdateTitle={title => updateTopicTitle(topic.id, title)} onDeleteTopic={() => deleteTopic(topic.id)} onMoveTopic={dir => moveTopic(topic.id, dir)} onUpdateKU={(kuId, title) => updateKU(topic.id, kuId, title)} onDeleteKU={kuId => deleteKU(topic.id, kuId)} onAddKU={() => addKU(topic.id)} data-id="element-1648" />)}

                                {/* Add Topic Button */}
                                <motion.button initial={{
                        opacity: 0
                      }} animate={{
                        opacity: 1
                      }} transition={{
                        delay: topics.length * 0.08 + 0.2
                      }} onClick={addTopic} className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm font-medium text-text-tertiary hover:text-[#2563EB] hover:border-[#2563EB]/30 hover:bg-[#2563EB]/[0.02] transition-all" data-id="element-1649">
                                  <PlusIcon size={16} data-id="element-1650" />
                                  Add Topic
                                </motion.button>
                              </div>}
                          </div>
                        </div> : <div className="flex items-start gap-3 justify-end" data-id="element-1651">
                          <div className="bg-[#2563EB] text-white rounded-xl rounded-tr-sm px-4 py-3 max-w-[85%]" data-id="element-1652">
                            <p className="text-sm leading-relaxed" data-id="element-1653">
                              {msg.content}
                            </p>
                          </div>
                        </div>}
                    </motion.div>)}

                  {/* Thinking indicator for subsequent messages */}
                  {isThinking && planRevealed && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} className="flex items-start gap-3" data-id="element-1654">
                      <AiAvatar data-id="element-1655" />
                      <div className="bg-[#2563EB]/[0.04] border border-[#2563EB]/10 rounded-xl rounded-tl-sm px-4 py-3" data-id="element-1656">
                        <ThinkingIndicator data-id="element-1657" />
                      </div>
                    </motion.div>}
                </div>
              </div>

              {/* ─── Input Area (Sticky Bottom) ─── */}
              <div className="border-t border-custom bg-surface flex-shrink-0" data-id="element-1658">
                {/* Suggested Prompts */}
                {planRevealed && !isThinking && <div className="px-4 sm:px-6 pt-3 pb-1 flex items-center gap-2 overflow-x-auto scrollbar-hide" data-id="element-1659">
                    <span className="text-[10px] font-bold text-text-tertiary uppercase tracking-wider flex-shrink-0" data-id="element-1660">
                      Try:
                    </span>
                    {suggestedPrompts.map(prompt => <button key={prompt} onClick={() => {
                setUserInput(prompt);
                inputRef.current?.focus();
              }} className="flex-shrink-0 px-2.5 py-1 text-[11px] font-medium text-text-tertiary border border-custom rounded-full hover:text-[#2563EB] hover:border-[#2563EB]/30 hover:bg-[#2563EB]/5 transition-all" data-id="element-1661">
                        {prompt}
                      </button>)}
                  </div>}

                {/* Chat Input */}
                <div className="px-4 sm:px-6 py-3" data-id="element-1662">
                  <div className="flex items-center gap-2 bg-surface-elevated border border-custom rounded-xl px-3 focus-within:border-[#2563EB]/40 focus-within:ring-2 focus-within:ring-[#2563EB]/10 transition-all" data-id="element-1663">
                    <SparklesIcon size={16} className="text-text-tertiary flex-shrink-0" data-id="element-1664" />
                    <input ref={inputRef} type="text" value={userInput} onChange={e => setUserInput(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }} placeholder="Ask AI to refine the plan..." disabled={!planRevealed || isThinking} className="flex-1 h-11 bg-transparent text-sm text-text-primary placeholder-text-tertiary focus:outline-none disabled:opacity-50" style={{
                  fontSize: 'max(16px, 0.875rem)'
                }} data-id="element-1665" />
                    <button onClick={handleSendMessage} disabled={!userInput.trim() || isThinking || !planRevealed} className={`p-2 rounded-lg transition-all min-w-[36px] min-h-[36px] flex items-center justify-center ${userInput.trim() && !isThinking ? 'bg-[#2563EB] text-white hover:bg-[#1d4ed8]' : 'bg-gray-100 text-text-tertiary'}`} aria-label="Send message" data-id="element-1666">
                      <SendIcon size={16} data-id="element-1667" />
                    </button>
                  </div>
                </div>

                {/* Confirm CTA */}
                {planRevealed && topics.length > 0 && <div className="px-4 sm:px-6 pb-4" data-id="element-1668">
                    <button onClick={handleConfirm} disabled={isThinking} className={`w-full h-[52px] sm:h-[48px] text-base sm:text-sm font-semibold rounded-xl flex items-center justify-center gap-2 transition-all active:scale-[0.99] ${isThinking ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-[#2563EB] text-white hover:bg-[#1d4ed8] shadow-sm hover:shadow-md hover:shadow-[#2563EB]/15'}`} data-id="element-1669">
                      <CheckCircleIcon size={18} data-id="element-1670" />
                      Confirm Plan & Generate Journey
                    </button>
                    <p className="text-center text-[11px] text-text-tertiary mt-2" data-id="element-1671">
                      {totalTopics} topic{totalTopics !== 1 ? 's' : ''} ·{' '}
                      {totalKUs} knowledge unit{totalKUs !== 1 ? 's' : ''} · ~
                      {totalSlides} slides
                    </p>
                  </div>}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}